import React, { forwardRef, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';

/**
 * The CRM-field dropdown of the wizard's Map Fields step (#971, #972): a
 * searchable Popover + Command combobox over the whole field catalog.
 *
 * - `groups` is [{ id, label, fields }]; COMPANY / CONTACT / RELATIONSHIP /
 *   CUSTOM FIELDS in the standard wizard. "Do not import" is always first.
 * - `usedBy` maps a target key to the header of ANOTHER column that holds it.
 *   Such an option stays visible but is aria-disabled and says "Used by {header}".
 * - `onAddCustom`, when given, adds "+ Add custom field" as a separate
 *   "Actions" group pinned to the foot of the list. Choosing it only opens the
 *   dialog: it is never a value, so the column's target does not change.
 *
 * The popover is modal. The list lives in a portal outside the wizard dialog,
 * and a modal Dialog's scroll lock would otherwise swallow the mouse wheel over
 * it (and a body-level portal is never clipped by the wizard's scrolling body).
 * The trigger keeps `aria-label="CRM field for {header}"`.
 */
const ENTITY_LABELS = { contact: 'Contact', account: 'Company', company: 'Company', relationship: 'Relationship' };
const entityLabel = (entity, groupId) => ENTITY_LABELS[entity] || ENTITY_LABELS[groupId] || '';

const LmFieldCombobox = forwardRef(function LmFieldCombobox(
    { header, value, groups, usedBy = {}, onChange, onAddCustom, disabled = false }, ref,
) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    // Opening the add dialog must not hand focus back to the trigger on the way out.
    const addingRef = useRef(false);

    const all = groups.flatMap((g) => g.fields);
    const current = value ? all.find((f) => f.key === value) : null;

    const toggle = (next) => {
        setOpen(next);
        if (!next) setQuery('');
    };
    const pick = (key) => {
        onChange(key);
        toggle(false);
    };
    const add = () => {
        addingRef.current = true;
        toggle(false);
        onAddCustom();
    };

    return (
        <Popover open={open} onOpenChange={toggle} modal>
            <PopoverTrigger asChild>
                <button
                    ref={ref}
                    type="button"
                    role="combobox"
                    aria-label={`CRM field for ${header}`}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    disabled={disabled}
                    data-testid="lm-field-combobox"
                    onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); toggle(true); } }}
                    className="w-full h-[34px] px-2.5 flex items-center justify-between gap-2 border border-input rounded-lg bg-card text-left text-[12.5px] text-foreground hover:bg-muted/50 disabled:opacity-50">
                    <span className="flex items-center gap-1.5 min-w-0">
                        {value ? (
                            <>
                                <span className="truncate">{current?.label || value}</span>
                                {current?.is_custom && <CustomFieldBadge />}
                            </>
                        ) : (
                            <span className="text-muted-foreground">Do not import</span>
                        )}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start" sideOffset={4} collisionPadding={8}
                className="w-[max(var(--radix-popover-trigger-width),300px)] p-0"
                data-testid="lm-field-combobox-list"
                onCloseAutoFocus={(e) => {
                    if (addingRef.current) { e.preventDefault(); addingRef.current = false; }
                }}>
                <Command loop>
                    <CommandInput
                        value={query} onValueChange={setQuery}
                        placeholder="Search CRM fields..." aria-label={`Search CRM fields for ${header}`} />
                    <CommandList className="max-h-[280px]">
                        <CommandEmpty>No CRM field matches “{query}”</CommandEmpty>
                        <CommandGroup>
                            <CommandItem value="Do not import" onSelect={() => pick(null)} data-testid="lm-field-option-none">
                                <span className="flex-1">Do not import</span>
                                {!value && <Check className="w-3.5 h-3.5" aria-label="Selected" />}
                            </CommandItem>
                        </CommandGroup>
                        {groups.filter((g) => g.fields.length > 0).map((g) => (
                            <CommandGroup key={g.id} heading={g.label}>
                                {g.fields.map((f) => {
                                    const holder = usedBy[f.key];
                                    const taken = !!holder && holder !== header;
                                    // A Contact and a Company custom field can share one `cf_<key>`: the
                                    // entity keeps their React keys and cmdk values apart (cmdk highlights
                                    // by value), and names which is which.
                                    const kind = f.is_custom ? entityLabel(f.entity, g.id) : null;
                                    return (
                                        <CommandItem
                                            key={`${f.entity || g.id}:${f.key}`}
                                            value={`${f.label} ${f.key}${kind ? ` ${kind}` : ''}`}
                                            disabled={taken}
                                            onSelect={() => pick(f.key)}
                                            data-entity={f.entity || undefined}
                                            data-testid={`lm-field-option-${f.key}`}>
                                            <span className="flex-1 min-w-0 flex items-center gap-1.5">
                                                <span className="truncate">{f.label}</span>
                                                {f.is_custom && <CustomFieldBadge />}
                                                {kind && <span className="shrink-0 text-[11.5px] text-muted-foreground" data-testid="lm-field-kind">{kind}</span>}
                                            </span>
                                            {taken && <span className="shrink-0 text-[11.5px] text-muted-foreground">Used by {holder}</span>}
                                            {value === f.key && <Check className="w-3.5 h-3.5" aria-label="Selected" />}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        ))}
                        {onAddCustom && (
                            <CommandGroup
                                heading="Actions" forceMount
                                className="sticky bottom-0 z-10 border-t border-border bg-popover">
                                <CommandItem value="Add custom field" forceMount onSelect={add} data-testid="lm-field-add-custom">
                                    + Add custom field
                                </CommandItem>
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
});

export default LmFieldCombobox;
